import React, {
  ComponentPropsWithoutRef,
  useEffect,
  useRef,
} from 'react';
import {
  chakra,
  Heading,
  VStack,
  Stack,
} from '@chakra-ui/react';
import {
  useForm,
  useFormState,
} from 'react-final-form';
import PlaceSearch from '../../components/form/PlaceSearch';
import { COUNTRIES } from '../../utils/regions';
import IntakeConfig from '../../interfaces/IntakeConfig.interface';
import InputField from './form/InputField';
import {
  compose,
  regex,
  required,
} from './form/validators';
import SelectField from './form/SelectField';

const getCountryLabel = (c: string) => {
  return COUNTRIES.find(item => item.keyName === c)?.text || c;
};

const BusinessAddressView = ({ config }: {
  config: IntakeConfig,
}): JSX.Element => {
  const form = useForm();
  const formState = useFormState();
  const { country } = formState.values.address;
  const countryObject = COUNTRIES.find(item => item.keyName === country) || COUNTRIES[0];
  const lastCountry = useRef();
  useEffect(
    () => {
      // When country changes, reset state and postal code
      // But don't do it on mount
      if (lastCountry.current && lastCountry.current !== country) {
        form.batch(() => {
          form.change('address.stateCode', '');
          form.change('address.postalCode', '');
        });
      }
      lastCountry.current = country;
    },
    [country],
  );

  type SelectedPlace = Parameters<ComponentPropsWithoutRef<typeof PlaceSearch>['onSelect']>[0];
  const updateFormStateFromSearch = (value: SelectedPlace) => {
    if (!value) return;

    form.batch(() => {
      form.change('address.streetNumber', value.saneAddress.streetNumber);
      form.change('address.streetName', value.saneAddress.streetName);
      form.change('address.unit', value.saneAddress.unit);
      form.change('address.city', value.saneAddress.city);
      form.change('address.country', value.saneAddress.country);

      if (value.websiteUri) {
        if (!formState.values.url) {
          form.change('url', value.websiteUri);
        }
      }
    });
    // Done in two batches because changing the country resets the stateCode and postalCode
    form.batch(() => {
      form.change('address.stateCode', value.saneAddress.state);
      form.change('address.postalCode', value.saneAddress.postalCode);
    });

    // Mark all address fields as touched so form validation is visible immediately
    form.mutators.setFieldTouched('address.streetNumber', true);
    form.mutators.setFieldTouched('address.streetName', true);
    form.mutators.setFieldTouched('address.unit', true);
    form.mutators.setFieldTouched('address.city', true);
    form.mutators.setFieldTouched('address.country', true);
    form.mutators.setFieldTouched('address.stateCode', true);
    form.mutators.setFieldTouched('address.postalCode', true);
  };

  return (
    <fieldset>
      <legend>
        <Heading fontSize="2xl" mb={6}>Business Address</Heading>
      </legend>
      {/* @ts-ignore */}
      <VStack spacing={5} align="stretch">
        <PlaceSearch onSelect={updateFormStateFromSearch} />
        <hr />
        <Stack direction={{ base: 'column', md: 'row' }} spacing={5} w="100%">
          <InputField
            label="Street Number"
            name="address.streetNumber"
            placeholder="1234"
            maxLength={64}
            validate={required('Required')}
            flex={1}
          />

          <InputField
            label="Street Name"
            name="address.streetName"
            placeholder="Main St."
            maxLength={128}
            validate={required('Required')}
            flex={2}
          />

          <InputField
            name="address.unit"
            label="Unit"
            optional
            placeholder="100"
            maxLength={64}
            flex={1}
          />
        </Stack>
        <Stack direction={{ base: 'column', md: 'row' }} spacing={5} w="100%">
          <InputField
            name="address.city"
            label="City"
            validate={required('Required')}
            placeholder="Kelowna"
            maxLength={128}
          />
          <SelectField
            name="address.stateCode"
            label={<chakra.div textTransform="capitalize">{countryObject?.regionType}</chakra.div>}
            validate={required('Required')}
          >
            <option value="">Select your {countryObject?.regionType}</option>
            {countryObject.regions.map(item => (
              <option value={item.id} key={item.id}>{item.text}</option>
            ))}
          </SelectField>
        </Stack>
        <Stack direction={{ base: 'column', md: 'row' }} spacing={5} w="100%">
          <InputField
            label={countryObject.codeLabel}
            name="address.postalCode"
            placeholder={countryObject.codeText}
            validate={compose(
              required('Required'),
              regex(countryObject.regex, `Invalid value (${countryObject.mask})`),
            )}
          />
          {
            config.countries?.length > 1
              ? (
                <SelectField
                  label="Country"
                  name="address.country"
                >
                  {
                    config.countries.map(c => <option key={c} value={c}>{getCountryLabel(c)}</option>)
                  }
                </SelectField>
              )
              : false
          }
        </Stack>
      </VStack>
    </fieldset>
  );
};

export default BusinessAddressView;
